import { Accordion, Badge, PageStatus } from '@unified-checkout/design-system';
import { getCardLogo } from '@unified-checkout/kit-components';
import { useInfiniteScrolling } from 'apps/customer/src/hooks';
import { useTransactions } from 'apps/customer/src/services/inner_layout/transactions';
import { Transaction } from 'apps/customer/src/types';
import { categorizePurchasesByMonth } from 'apps/customer/src/utils';
import { format, parseISO } from 'date-fns';
import { LegacyRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircleExclamation } from "@unified-checkout/design-system/icons/index";
import { Spinner } from 'apps/customer/src/components';

interface IPurchaseListProps {
  page: number;
}
export const PurchaseList = ({ page }: IPurchaseListProps) => {
  const elements = [];
  const [searchParams] = useSearchParams();
  const isRefunded = searchParams.get('is_refunded') || undefined;
  const fromDate = searchParams.get('from') || '';
  const toDate = searchParams.get('to') || '';
  const getErrorMessage = (error: unknown): string => {
  if (!error) return 'Something went wrong';

  const err = error as any;

  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.response?.data?.errors[0] ||
    err?.message ||
    'We couldn’t load your purchase history. Please try again.'
  );
};

  const {
    data: transactionsRes,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPending,
    error
    } = useTransactions({
    page,
    is_refunded: isRefunded,
    from: fromDate,
    to: toDate,
  });

  const { lastItemElementRef } = useInfiniteScrolling({
    Data: transactionsRes,
    fetchNextPage: fetchNextPage,
    isFetchingNextPage: isFetchingNextPage,
    hasNextPage: hasNextPage,
  });

  const transactions = transactionsRes?.pages.flatMap((page) => page.results) || [];
  const purchaseMappedData = categorizePurchasesByMonth(transactions || []);
  const dateFormatter = (date: string) =>
    format(parseISO(date.replace(' ', 'T')), 'MMM d, yyyy, h:mm a');

  for (const key in purchaseMappedData) {
    if (Object.prototype.hasOwnProperty.call(purchaseMappedData, key)) {
      const element = purchaseMappedData[key];
      elements.push(
        <section key={key}>
          <h3 className="mb-3 text-gray-400 english-utility-label-semibold">
            {key.toUpperCase()}
          </h3>
          <section className="flex flex-col gap-2 [&_p]:text-gray-600">
            {element.map((transaction: Transaction) => (
              <div
                key={transaction.transaction_id}
                ref={lastItemElementRef as LegacyRef<HTMLDivElement>}
              >
                <Accordion
                  key={transaction.transaction_id}
                  header={
                    <>
                      <div className="w-[2.875rem] h-8 ">
                        <img src={transaction.logo} alt="merchant-logo" />
                      </div>
                      <div className="flex grow [&_p]:english-utility-label-compact-regular">
                        <section>
                          <div className="flex items-center gap-3 [&>[data-badge-color='gray']]:mix-blend-normal [&>div]:text-gray-600">
                            <h4 className="text-gray-900 english-utility-label-semibold">
                              {transaction.amount}
                            </h4>
                            {(transaction.is_refunded || transaction?.is_voided) && (
                              <Badge
                                color="gray"
                                text="Refunded"
                                type="default"
                                size="sm"
                              />
                            )}
                          </div>
                          <p className="mt-2 mb-1">
                            {dateFormatter(transaction.created_at)}
                          </p>
                          <p>{transaction.merchant}</p>
                        </section>
                      </div>
                    </>
                  }
                >
                  <section className="w-fit ms-[3.625rem] mt-1 [&_p]:english-utility-label-compact-regular">
                    <p>Transaction ID : {transaction.transaction_id}</p>
                    <div className="flex items-center gap-2 mt-2 text-gray-600 english-utility-label-compact-regular">
                      <div className="[&_svg]:w-7 [&_svg]:h-5">
                        {getCardLogo(transaction.sub_type)}
                      </div>
                      <p>
                        {transaction.sub_type} ****{transaction.pan}
                      </p>
                    </div>
                  </section>
                  {(transaction.is_refunded || transaction?.is_voided) && transaction.refunds ? (
                    <section className="ms-[3.625rem] mt-4">
                      <h4 className="mb-5 text-gray-600 english-utility-label-compact-semibold">
                        Refund details :
                      </h4>
                      <section className="!space-y-2.5 [&_p]:english-body-sm-regular">
                        {transaction.refunds.map((refund, index) => (
                          <section key={refund.refund_transaction_id}>
                            <h5 className="mb-2 text-gray-600 english-utility-label-compact-regular">
                              REFUND {index + 1}
                            </h5>
                            <p>Refund amount : {refund.refund_amount}</p>
                            <p>Refunded on : {refund.refunded_on}</p>
                            <p>
                              Refund txn ID : {refund.refund_transaction_id}
                            </p>
                          </section>
                        ))}
                        <h5 className="text-gray-600 english-utility-label-compact-semibold">
                          Total Refund : {transaction.total_refund}
                        </h5>
                      </section>
                    </section>
                  ) : (
                    <></>
                  )}
                </Accordion>
              </div>
            ))}
          </section>
        </section>
      );
    }
  }
  if (!isPending && error) {
    return (
      <div className='flex mt-20 items-center'>
        <PageStatus status='error' title='Something went wrong!' description='An error occurred while retrieving data, Please try again' icon={CircleExclamation} />
      </div>
    );
  }
  if (elements.length === 0 && !isPending) return <p>No purchase history</p>;
  if(isPending && !error) return  <Spinner />
  return <> {elements}</>;
};
